import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  ApiError,
  createDepartment,
  type DepartmentOut,
  fetchBusinessLines,
  fetchDepartments,
  searchEmployees,
  updateDepartment,
} from './api'

/**
 * Раскладывает плоский список в порядок обхода дерева, попутно считая глубину.
 *
 * Сирота (родитель есть, но в выдачу не попал) не теряется, а показывается на верхнем
 * уровне: для роли «сотрудник» видимость сужена сервером, и родитель может быть просто
 * не виден — молча спрятать при этом дочернее подразделение было бы хуже.
 */
function asTree(departments: DepartmentOut[]): { department: DepartmentOut; depth: number }[] {
  const byParent = new Map<number | null, DepartmentOut[]>()
  const known = new Set(departments.map((department) => department.id))

  for (const department of departments) {
    const parent =
      department.parent_id !== null && known.has(department.parent_id)
        ? department.parent_id
        : null
    byParent.set(parent, [...(byParent.get(parent) ?? []), department])
  }

  const rows: { department: DepartmentOut; depth: number }[] = []
  const walk = (parent: number | null, depth: number) => {
    for (const department of byParent.get(parent) ?? []) {
      rows.push({ department, depth })
      walk(department.id, depth + 1)
    }
  }
  walk(null, 0)
  return rows
}

function CreateDepartmentForm({ departments }: { departments: DepartmentOut[] }) {
  const queryClient = useQueryClient()

  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [parentId, setParentId] = useState('')
  const [lineId, setLineId] = useState('')
  const [collapsed, setCollapsed] = useState(false)

  const lines = useQuery({ queryKey: ['business-lines'], queryFn: fetchBusinessLines })

  const save = useMutation({
    mutationFn: () =>
      createDepartment({
        code,
        name,
        parent_id: parentId ? Number(parentId) : null,
        business_line_id: lineId ? Number(lineId) : null,
        is_collapsed: collapsed,
      }),
    onSuccess: () => {
      setCode('')
      setName('')
      setParentId('')
      setLineId('')
      setCollapsed(false)
      queryClient.invalidateQueries({ queryKey: ['departments'] })
      queryClient.invalidateQueries({ queryKey: ['chart'] })
      queryClient.invalidateQueries({ queryKey: ['audit'] })
    },
  })

  const error = save.error instanceof ApiError ? save.error.message : null

  return (
    <form
      className="card"
      onSubmit={(event) => {
        event.preventDefault()
        save.mutate()
      }}
    >
      <h2>Новое подразделение</h2>

      <div className="inline-fields">
        <label className="field">
          <span>Код</span>
          <input
            required
            value={code}
            placeholder="Отдел разработки"
            onChange={(event) => setCode(event.target.value)}
          />
        </label>

        <label className="field">
          <span>Название</span>
          <input required value={name} onChange={(event) => setName(event.target.value)} />
        </label>

        <label className="field">
          <span>Входит в</span>
          <select value={parentId} onChange={(event) => setParentId(event.target.value)}>
            <option value="">— верхний уровень</option>
            {departments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.code}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Направление</span>
          <select value={lineId} onChange={(event) => setLineId(event.target.value)}>
            <option value="">—</option>
            {(lines.data ?? []).map((line) => (
              <option key={line.id} value={line.id}>
                {line.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="toggle">
        <input
          type="checkbox"
          checked={collapsed}
          onChange={(event) => setCollapsed(event.target.checked)}
        />
        Показывать на схеме одним узлом с численностью
      </label>

      <p className="muted small">
        Свёрнутое подразделение не выводит своих людей на схему поимённо — крупный отдел
        иначе делает её нечитаемой. Найти человека оттуда по-прежнему можно поиском.
      </p>

      {error && <p className="bad form-error">{error}</p>}

      <button type="submit" disabled={save.isPending || !code.trim() || !name.trim()}>
        {save.isPending ? 'Создаём…' : 'Создать'}
      </button>
    </form>
  )
}

function DepartmentRow({
  department,
  depth,
  departments,
}: {
  department: DepartmentOut
  depth: number
  departments: DepartmentOut[]
}) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(department.name)
  const [parentId, setParentId] = useState(
    department.parent_id === null ? '' : String(department.parent_id),
  )
  const [lineId, setLineId] = useState(
    department.business_line_id === null ? '' : String(department.business_line_id),
  )
  const [collapsed, setCollapsed] = useState(department.is_collapsed)
  const [headQuery, setHeadQuery] = useState('')
  const [headId, setHeadId] = useState<number | null>(department.head_employee_id)

  const lines = useQuery({ queryKey: ['business-lines'], queryFn: fetchBusinessLines })

  const headOptions = useQuery({
    queryKey: ['head-search', headQuery],
    queryFn: () => searchEmployees(headQuery),
    enabled: editing && headQuery.trim().length >= 2,
  })

  const save = useMutation({
    mutationFn: () =>
      updateDepartment(department.id, {
        name,
        parent_id: parentId ? Number(parentId) : null,
        business_line_id: lineId ? Number(lineId) : null,
        is_collapsed: collapsed,
        head_employee_id: headId,
      }),
    onSuccess: () => {
      setEditing(false)
      queryClient.invalidateQueries({ queryKey: ['departments'] })
      queryClient.invalidateQueries({ queryKey: ['chart'] })
      queryClient.invalidateQueries({ queryKey: ['audit'] })
    },
  })

  const error = save.error instanceof ApiError ? save.error.message : null

  if (editing) {
    return (
      <tr>
        <td colSpan={5}>
          <form
            className="department-edit"
            onSubmit={(event) => {
              event.preventDefault()
              save.mutate()
            }}
          >
            <div className="inline-fields">
              <label className="field">
                <span>Название</span>
                <input value={name} onChange={(event) => setName(event.target.value)} />
              </label>

              <label className="field">
                <span>Входит в</span>
                <select value={parentId} onChange={(event) => setParentId(event.target.value)}>
                  <option value="">— верхний уровень</option>
                  {departments
                    .filter((row) => row.id !== department.id)
                    .map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.code}
                      </option>
                    ))}
                </select>
              </label>

              <label className="field">
                <span>Направление</span>
                <select value={lineId} onChange={(event) => setLineId(event.target.value)}>
                  <option value="">—</option>
                  {(lines.data ?? []).map((line) => (
                    <option key={line.id} value={line.id}>
                      {line.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="toggle">
                <input
                  type="checkbox"
                  checked={collapsed}
                  onChange={(event) => setCollapsed(event.target.checked)}
                />
                Показывать одним узлом с численностью
              </label>

              <label className="field">
                <span>Руководитель</span>
                <input
                  value={headQuery}
                  placeholder="Начните вводить имя…"
                  onChange={(event) => {
                    setHeadQuery(event.target.value)
                    setHeadId(null)
                  }}
                />
              </label>
            </div>

            {headOptions.data && headQuery.trim().length >= 2 && headId === null && (
              <ul className="search-results inline">
                {headOptions.data.map((candidate) => (
                  <li key={candidate.id}>
                    <button
                      type="button"
                      className="link"
                      onClick={() => {
                        setHeadId(candidate.id)
                        setHeadQuery(candidate.display_name)
                      }}
                    >
                      {candidate.display_name}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {error && <p className="bad form-error">{error}</p>}

            <div className="actions-left">
              <button type="submit" disabled={save.isPending}>
                Сохранить
              </button>
              <button type="button" onClick={() => setEditing(false)}>
                Отмена
              </button>
            </div>
          </form>
        </td>
      </tr>
    )
  }

  return (
    <tr>
      <td>
        <span style={{ paddingLeft: `${depth * 20}px` }}>{department.code}</span>
      </td>
      <td>{department.name}</td>
      <td>{department.business_line ?? '—'}</td>
      <td className="nowrap">
        {department.employee_count}
        {department.is_collapsed && <span className="badge">свёрнуто</span>}
      </td>
      <td className="actions">
        <button
          type="button"
          onClick={() => {
            setName(department.name)
            setParentId(department.parent_id === null ? '' : String(department.parent_id))
            setLineId(
              department.business_line_id === null ? '' : String(department.business_line_id),
            )
            setCollapsed(department.is_collapsed)
            setHeadId(department.head_employee_id)
            setHeadQuery('')
            setEditing(true)
          }}
        >
          Редактировать
        </button>
      </td>
    </tr>
  )
}

export default function DepartmentsPage() {
  const departments = useQuery({ queryKey: ['departments'], queryFn: fetchDepartments })

  return (
    <section>
      {departments.data && <CreateDepartmentForm departments={departments.data} />}

      <div className="card">
        <h2>Подразделения</h2>

        {departments.isPending && <p>Загружаем…</p>}
        {departments.isError && <p className="bad">Не удалось загрузить список</p>}

        {departments.data && (
          <table>
            <thead>
              <tr>
                <th>Код</th>
                <th>Название</th>
                <th>Бизнес-линия</th>
                <th>Сотрудников</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {asTree(departments.data).map(({ department, depth }) => (
                <DepartmentRow
                  key={department.id}
                  department={department}
                  depth={depth}
                  departments={departments.data}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  )
}
